<template>
  <Layout
    :id="page.id"
    class="onboarding-options"
    :class="{ 'onboarding-options--20710': isSampleFirstTreatment }"
    :title="page.title"
    :subtitle="page.subtitle"
    :scrollDefaultSlot="true"
    :reverseTitleSubtitle="!isSampleFirstTreatment"
  >
    <OptionsBody
      v-model="selectedOptions"
      :page="page"
      :options="options"
      :skip-option="skipOption"
      :image-src="image"
      :sampleFirst="isSampleFirstTreatment"
      @skip="skip"
    />
  </Layout>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'

import { assertNotNull } from '@/helpers/typing'
import { getImage, QuestionnaireView } from '@/models/onboarding'
import type {
  OptionsPage,
  QuestionnaireFormOption,
} from '@/models/onboarding.types'
import { sampleFirstOnboardingV10_20710Variant } from '@/services/ab/experiments'

import Layout from '../Layout.vue'
import OptionsBody from './OptionsBody.vue'

const props = defineProps<{
  view: QuestionnaireView
}>()

const isSampleFirstTreatment = computed(
  () => sampleFirstOnboardingV10_20710Variant() === 'B',
)

const page = computed(() => {
  return props.view.currentPage as OptionsPage
})

const selectedOptions = ref<string[]>([])

watch(selectedOptions, (newValue) => {
  if (page.value.multipleOptions) {
    props.view.setCurrentChoice(newValue)
  } else {
    // `selectedOptions` is an array to make it easier
    // to handle multiple-option pages, but if this
    // is not a multi-option page, then `selectedOptions`
    // should only have one item.
    props.view.setCurrentChoice(newValue[0])
  }
})

const options = computed(() => {
  // Remove the `skip` option. We handle it differently than the other options.
  return page.value.options.filter((option) => option.type !== 'skip')
})

const skipOption = computed(() => {
  return page.value.options.find(
    (option) => typeof option !== 'string' && option.type === 'skip',
  ) as QuestionnaireFormOption | undefined
})

const image = computed(() => {
  if (!page.value.image) {
    return
  }

  return getImage(page.value.image.src)
})

/**
 * Set `selectedOptions` to the value of the `skipOption`,
 * and navigate to the next page.
 */
async function skip(): Promise<void> {
  selectedOptions.value = [assertNotNull(skipOption.value?.label)]
  // Wait for `watch`.
  await nextTick()

  await props.view.goNextPage()
}
</script>

<style lang="scss" scoped>
.onboarding-options {
  :deep(.onboarding-layout__default-slot) {
    display: flex;
    flex-direction: column;
  }

  :deep(.onboarding-layout__text) {
    margin-bottom: 28px;
  }
}

.onboarding-options--20710 {
  :deep(.onboarding-layout__text),
  :deep(.onboarding-layout__default-slot > *) {
    width: 100%;
    max-width: none;
    margin-inline: auto;

    @media (min-width: $w-pc-b) {
      max-width: 600px;
    }
  }
}

.onboarding-options:not(.onboarding-options--20710) {
  :deep(.onboarding-layout__subtitle) {
    color: $c-black-15;
    font-style: italic;
  }
}

#commit .onboarding-options {
  :deep(.onboarding-options__options) {
    margin-top: 0;
  }

  :deep(.onboarding-options__image) {
    min-height: 100px;
    margin-left: auto;

    @media only screen and (min-height: 825px) {
      max-height: none;
      margin-top: 48px;
    }
  }
}
</style>
